import { html } from "lit";
import { Router } from "@vaadin/router";
import { StateView } from "./views/state-view.js";
import { authService } from './services/auth/index.js';
import { authMonitor } from './services/auth/auth-monitor.js';
import { orgService } from './services/org-service.js';
import './components/shared/global-drag-drop.js';
import './components/shared/toast-notifications.js';
import './components/shared/help-sidebar.js';
import { normalizeMemberPermissions, hasSidebarAccess } from './config/member-permissions.js';
import { tokenProvider } from './services/auth/token-provider.js';

export class AppRoot extends StateView {
  static properties = {
    menuOpen: { type: Boolean },
    mobileMenuOpen: { type: Boolean },
    helpSidebarOpen: { type: Boolean }
  };

  constructor() {
    super();
    this.menuOpen = false;
    this.mobileMenuOpen = false;
    this.helpSidebarOpen = false;
  }

  connectedCallback() {
    super.connectedCallback();
    this._seedPermissionsFromSession();
  }

  stateChanged(state) {
    super.stateChanged(state);
    this._ensureEffectivePermissions(state);
    this.requestUpdate();
    if (this.permissionsReady && this.effectivePermissions) {
      this._enforceCurrentRoutePermissions();
    }
  }

  _handleShowNotification = (e) => {
    const { message, type = 'info', duration = 5000 } = e.detail || {};
    const notifier = document.querySelector('toast-notifications') || window.__toastNotifications;
    if (notifier && typeof notifier.show === 'function' && message) {
      notifier.show(message, type, duration);
      // Prevent further default handling if any
      e.preventDefault?.();
    }
  }

  _notifyAccessDenied(message = 'You do not have access to that area.', type = 'warning', duration = 4000) {
    const notifier = this.renderRoot?.querySelector('toast-notifications') || document.querySelector('toast-notifications') || window.__toastNotifications;
    if (notifier && typeof notifier.show === 'function') {
      notifier.show(message, type, duration);
    }
  }

  _handleNavClick(event, permissionKey, fallbackPath) {
    if (typeof fallbackPath === 'undefined') {
      fallbackPath = permissionKey === 'chat' ? '/' : '/me';
    }
    if (!this._hasSidebarAccess(permissionKey)) {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      this._notifyAccessDenied();
      const currentPath = window?.location?.pathname;
      if (fallbackPath && currentPath !== fallbackPath) {
        Router.go(fallbackPath);
      }
      return false;
    }
    return true;
  }

  _ensureRouteAccess(permissionKey, fallbackPath) {
    if (typeof fallbackPath === 'undefined') {
      fallbackPath = permissionKey === 'chat' ? '/' : '/me';
    }
    if (!this._hasSidebarAccess(permissionKey)) {
      this._notifyAccessDenied();
      const currentPath = window?.location?.pathname;
      if (fallbackPath && currentPath !== fallbackPath) {
        Router.go(fallbackPath);
      }
      return false;
    }
    return true;
  }

  async firstUpdated() {
    try {
      // Wait for outlet element
      await this.updateComplete;
      
      // Set up sidebar animation if authenticated
      if (this.authenticated) {
        this.setupSidebarAnimation();
      }
      
      // Initialize auth service first and wait for it
      const authResult = await authService.init();
      console.log('Auth service initialized:', authResult);
      
      // Set authentication state based on auth service result, but do not regress existing pre-seeded state
      if (authResult.isAuthenticated) {
        this.setState({
          authenticated: true,
          user: authResult.userData || this.user
        });
      } else if (!this.user) {
        this.setState({ authenticated: false, user: null });
      }
      
      // Start auth monitoring if authenticated
      if (authResult.isAuthenticated) {
        authMonitor.startMonitoring();
        console.log('Auth monitoring started');
        const memberProfile = await orgService.getLoggedInMember(authResult.orgId, authResult.userData.id);
        if (memberProfile) {
          let rawPermissions = memberProfile.permissions;
          if (!rawPermissions && memberProfile.permissions_json) {
            try {
              rawPermissions = JSON.parse(memberProfile.permissions_json);
            } catch (error) {
              console.warn('[AppRoot] Failed to parse permissions_json for logged-in member profile.', error);
            }
          }
          memberProfile.permissions = normalizeMemberPermissions(rawPermissions, memberProfile.role || authResult.userData.role);
          try {
            memberProfile.permissions_json = JSON.stringify(memberProfile.permissions);
          } catch (_) {
            /* ignore JSON stringify errors */
          }

        }
        this.setState({
          currentMemberProfile: memberProfile || null
        });
        if (memberProfile?.permissions) {
          this._setEffectivePermissions(memberProfile.permissions, true);
          try {
            const sessionSnapshot = tokenProvider.getSessionData?.();
            if (sessionSnapshot?.sessionToken) {
              const updatedSession = {
                ...sessionSnapshot,
                userData: {
                  ...(sessionSnapshot.userData || {}),
                  permissions: memberProfile.permissions,
                  permissions_json: (() => {
                    try { return JSON.stringify(memberProfile.permissions); } catch (_) { return sessionSnapshot.userData?.permissions_json; }
                  })(),
                  role: memberProfile.role || sessionSnapshot.userData?.role || authResult.userData.role
                }
              };
              tokenProvider.setSessionData(updatedSession);
            }
          } catch (error) {
            console.warn('[AppRoot] Failed to persist member permissions to session.', error);
          }
        }
        
        // Set up sidebar animation after authentication confirmed
        this.setupSidebarAnimation();
      }
      
      // Get outlet element for router (inside shadow DOM)
      const outlet = this.renderRoot?.querySelector('#outlet');
      if (!outlet) {
        throw new Error('Router outlet not found');
      }
      
      // Set up router
      const router = new Router(outlet);
      
      // Define public paths that don't require auth
      const publicPaths = ['/', '/login', '/auth/verify', '/docs', '/faq', '/contact', '/terms', '/privacy', '/shared'];

      router.setRoutes([
        {
          path: "/debug/agent-turns",
          component: "debug-agent-turns-view",
          action: async () => {
            if (!this.authenticated) {
              sessionStorage.setItem('redirectPath', '/debug/agent-turns');
              Router.go('/login');
              return;
            }
            await import('./views/debug-agent-turns-view.js');
          }
        },
        {
          path: "/",
          component: "marketing-view",
          action: () => import("./views/marketing-view.js")
        },
        {
          path: "/login",
          component: "login-view",
          action: () => import("./views/login-view.js")
        },
        {
          path: "/shared/:token",
          component: "shared-chat-view",
          action: async () => {
            // Public route - no auth required
            await import("./views/shared-chat-view.js");
          }
        },
        {
          path: "/auth/verify",
          component: "verify-view",
          action: async () => {
            console.log('Loading verify view, current URL:', window.location.href);
            try {
              await import("./views/verify-view.js");
              console.log('Verify view loaded successfully');
              
              // Check if we have a token in the URL
              const params = new URLSearchParams(window.location.search);
              if (!params.get('token')) {
                console.log('No token found in URL, redirecting to login');
                Router.go('/login?error=No verification token found. Please request a new login link.');
                return;
              }
            } catch (error) {
              console.error('Error loading verify view:', error);
              Router.go('/login?error=Failed to load verification page. Please try again.');
            }
          }
        },
        {
          path: "/auth/desktop-redirect",
          component: "app-redirect-view",
          action: async () => {
            console.log('Loading desktop redirect view, current URL:', window.location.href);
            try {
              await import("./views/app-redirect-view.js");
              console.log('Desktop redirect view loaded successfully');
              
              // Check if we have token and orgId in the URL
              const params = new URLSearchParams(window.location.search);
              if (!params.get('token') || !params.get('orgId')) {
                console.log('Missing required parameters, redirecting to login');
                Router.go('/login?error=Invalid desktop app link. Please request a new login link.');
                return;
              }
            } catch (error) {
              console.error('Error loading desktop redirect view:', error);
              Router.go('/login?error=Failed to load desktop redirect page. Please try again.');
            }
          }
        },
        {
          path: "/me",
          component: "chat-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /me route');
              sessionStorage.setItem('redirectPath', '/me');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('chat', '/')) return;
            await import("./views/chat-view.js");
          }
        },
        {
          path: "/help",
          component: "help-view",
          action: async () => {
            if (!this.authenticated) {
              sessionStorage.setItem('redirectPath', '/help');
              Router.go('/login');
              return;
            }
            if (!this._ensureRouteAccess('help')) return;
            await import('./views/help-view.js');
          }
        },
        {
          path: "/terms",
          component: "tos-view",
          action: () => import("./views/tos-view.js")
        },
        {
          path: "/privacy",
          component: "privacy-view",
          action: () => import("./views/privacy-view.js")
        },
        {
          path: "/settings",
          component: "settings-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /settings route');
              sessionStorage.setItem('redirectPath', '/settings');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('settings')) return;
            await import("./views/settings-view.js");
          }
        },
        {
          path: "/media",
          component: "media-view",
          action: async () => {
            if (!this.authenticated) {
              sessionStorage.setItem('redirectPath', '/media');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('media')) return;
            await import("./views/media-view.js");
          }
        },
        {
          path: "/browser",
          component: "browser-view",
          action: async () => {
            if (!this.authenticated) {
              sessionStorage.setItem('redirectPath', '/browser');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('browser')) return;
            await import("./views/browser-view.js");
          }
        },
        {
          path: "/documents",
          component: "document-manager",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /documents route');
              sessionStorage.setItem('redirectPath', '/documents');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('documents')) return;
            await import("./components/knowledge/document-manager.js");
          }
        },
        {
          path: "/queues",
          component: "queue-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /queues route');
              sessionStorage.setItem('redirectPath', '/queues');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('queues')) return;
            await import("./views/queue-view.js");
          }
        },
        {
          path: "/graph",
          component: "graph-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /graph route');
              sessionStorage.setItem('redirectPath', '/graph');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('graph')) return;
            await import("./views/graph-view.js");
          }
        },
        {
          path: "/tables",
          component: "table-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /tables route');
              sessionStorage.setItem('redirectPath', '/tables');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('tables')) return;
            await import("./views/table-view.js");
          }
        },
        {
          path: "/artifacts",
          component: "artifacts-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /artifacts route');
              sessionStorage.setItem('redirectPath', '/artifacts');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('artifacts')) return;
            await import("./views/artifacts-view.js");
          }
        },
        {
          path: "/chat",
          component: "chat-view",
          action: async () => {
            if (!this._ensureRouteAccess('chat', '/')) return;
            await import("./views/chat-view.js");
          }
        },
        {
          path: "/tools",
          component: "tools-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /tools route');
              sessionStorage.setItem('redirectPath', '/tools');
              Router.go("/login");
              return;
            }
            await import("./views/tools-view.js");
          }
        },
        {
          path: "/tools/create",
          component: "tool-create-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /tools/create route');
              sessionStorage.setItem('redirectPath', '/tools/create');
              Router.go("/login");
              return;
            }
            await import("./views/tool-create-view.js");
          }
        },
        {
          path: "/tools/:toolId/edit",
          component: "tool-edit-view",
          action: async (context) => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /tools/edit route');
              sessionStorage.setItem('redirectPath', `/tools/${context.params.toolId}/edit`);
              Router.go("/login");
              return;
            }
            await import("./views/tool-edit-view.js");
          }
        },
        {
          path: "/tools/:toolId",
          component: "tool-detail-view",
          action: async (context) => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /tools/detail route');
              sessionStorage.setItem('redirectPath', `/tools/${context.params.toolId}`);
              Router.go("/login");
              return;
            }
            await import("./views/tool-detail-view.js");
          }
        },
        {
          path: "/metrics",
          component: "admin-view",
          action: async () => {
            if (!this.authenticated) {
              console.log('Not authenticated, redirecting to login from /metrics route');
              sessionStorage.setItem('redirectPath', '/metrics');
              Router.go("/login");
              return;
            }
            if (!this._ensureRouteAccess('metrics')) return;
            await import("./views/admin-view.js");
          }
        },
        // {
        //   path: "/opfs",
        //   component: "opfs-view",
        //   action: async () => {
        //     if (!this.authenticated) {
        //       console.log('Not authenticated, redirecting to login from /opfs route');
        //       sessionStorage.setItem('redirectPath', '/opfs');
        //       Router.go("/login");
        //       return;
        //     }
        //     await import("./views/opfs-view.js");
        //   }
        // },
        {
          path: "(.*)",
          component: "view-404",
          action: () => import("./views/view-404.js")
        }
      ]);

      // Set up router subscription for navigation
      router.subscribe(() => {
        const currentPath = router.location.pathname;
        const isPublicPath = publicPaths.some(path => currentPath.startsWith(path));
        console.log('Route changed:', currentPath, 'Public path:', isPublicPath, 'Authenticated:', this.authenticated);
        
        // Skip auth checks for verify path
        if (currentPath === '/auth/verify') {
          console.log('On verify path, skipping auth checks');
          return;
        }
        
        // Only redirect unauthenticated users trying to access protected resources
        if (!this.authenticated && !isPublicPath) {
          console.log('Not authenticated, redirecting to login');
          // Store the intended destination for post-login redirect
          sessionStorage.setItem('redirectPath', currentPath);
          Router.go('/login');
        }
        // Don't automatically redirect authenticated users from public paths unless specifically at login
        else if (this.authenticated && currentPath === '/login') {
          console.log('Already authenticated, redirecting to dashboard');
          Router.go('/me');
        }

        if (this.authenticated) {
          this._enforceCurrentRoutePermissions();
        }
      });

      // Handle initial navigation after auth check
      const currentPath = window.location.pathname;
      const isPublicPath = publicPaths.some(path => currentPath.startsWith(path));
      console.log('Initial navigation:', currentPath, 'Public path:', isPublicPath, 'Authenticated:', this.authenticated);
      
      // Skip initial navigation check for verify path
      if (currentPath === '/auth/verify') {
        console.log('On verify path, proceeding with verification');
        return;
      }
      
      // Only redirect from protected resources if not authenticated
      if (!this.authenticated && !isPublicPath) {
        console.log('Not authenticated on protected path, redirecting to login');
        // Store the intended destination for post-login redirect
        sessionStorage.setItem('redirectPath', currentPath);
        Router.go('/login');
      }
      // Only redirect from login if authenticated
      else if (this.authenticated && currentPath === '/login') {
        console.log('Already authenticated on login page, redirecting to dashboard');
        Router.go('/me');
      }

      if (this.authenticated) {
        this._enforceCurrentRoutePermissions();
      }

    } catch (error) {
      console.error('Error initializing app:', error);
      this.setState({
        user: null,
        authenticated: false,
        sessionToken: null
      });
      Router.go('/login');
    }
  }

  async handleLogout() {
    try {
      // Stop the auth monitor
      authMonitor.stopMonitoring();
      
      await authService.logout();
      this.setState({
        user: null,
        authenticated: false,
        sessionToken: null
      });
      Router.go('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  toggleChat = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({chatOpen: !this.chatOpen});
  }

  closeChat = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.setState({chatOpen: false});
  }

  _getMemberPermissions() {
    if (this.permissionsReady && this.effectivePermissions) {
      return this.effectivePermissions;
    }

    const profile = this.currentMemberProfile;
    const session = tokenProvider.getSessionData?.();
    const role = profile?.role || this.user?.role || session?.userData?.role || 'member';

    let rawPermissions = profile?.permissions;
    if (!rawPermissions && profile?.permissions_json) {
      try {
        rawPermissions = JSON.parse(profile.permissions_json);
      } catch (error) {
        console.warn('[AppRoot] Failed to parse permissions_json for member profile.', error);
      }
    }

    // If we have session data with permissions, use it as fallback
    if (!rawPermissions && session?.userData?.permissions) {
      rawPermissions = session.userData.permissions;
    }
    if (!rawPermissions && session?.userData?.permissions_json) {
      try {
        rawPermissions = JSON.parse(session.userData.permissions_json);
      } catch (error) {
        console.warn('[AppRoot] Failed to parse permissions_json from session.', error);
      }
    }

    if (!rawPermissions) {
      return null;
    }

    const normalized = normalizeMemberPermissions(rawPermissions, role);
    if (normalized) {
      this._setEffectivePermissions(normalized, true);
    }
    return normalized;
  }

  _hasSidebarAccess(key) {
    const permissions = this.effectivePermissions || this._getMemberPermissions();
    if (!permissions) return false;
    return hasSidebarAccess(permissions, key);
  }

  _routePermissionKeyForPath(pathname = '/') {
    if (!pathname) return null;
    const mapping = [
      { prefix: '/me', key: 'chat' },
      { prefix: '/chat', key: 'chat' },
      { prefix: '/documents', key: 'documents' },
      { prefix: '/queues', key: 'queues' },
      { prefix: '/graph', key: 'graph' },
      { prefix: '/tables', key: 'tables' },
      { prefix: '/artifacts', key: 'artifacts' },
      { prefix: '/media', key: 'media' },
      { prefix: '/browser', key: 'browser' },
      { prefix: '/metrics', key: 'metrics' },
      { prefix: '/settings', key: 'settings' },
      { prefix: '/help', key: 'help' }
    ];

    return mapping.find(({ prefix }) => pathname === prefix || pathname.startsWith(`${prefix}/`))?.key || null;
  }

  _enforceCurrentRoutePermissions() {
    if (!this.authenticated) return;
    if (!this.permissionsReady || !this._getMemberPermissions()) return;
    const path = window?.location?.pathname || Router?.location?.pathname || '/';
    const key = this._routePermissionKeyForPath(path);
    if (!key) return;
    if (!this._hasSidebarAccess(key)) {
      const fallback = key === 'chat' ? '/' : '/me';
      this._notifyAccessDenied();
      if (path !== fallback) {
        Router.go(fallback);
      }
    }
  }

  setupSidebarAnimation() {
    // Wait for the sidebar canvas to be in the DOM
    setTimeout(() => {
      const canvas = this.renderRoot.querySelector('#sidebarCanvas');
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match the sidebar
      const sidebar = this.renderRoot.querySelector('.sidebar-container');
      if (sidebar) {
        canvas.width = sidebar.offsetWidth;
        canvas.height = sidebar.offsetHeight;
      } else {
        canvas.width = 80; // Default sidebar width
        canvas.height = window.innerHeight;
      }
      
      // Increase particle count and make them more visible
      const particles = Array.from({ length: 30 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        dx: (Math.random() - 0.5) * 0.25,
        dy: (Math.random() - 0.5) * 0.25,
        radius: Math.random() * 1.8 + 0.5, // Larger particles
        brightness: Math.random() * 0.6 + 0.4,
        pulse: Math.random() * 0.02 + 0.01,
        pulseDirection: Math.random() > 0.5 ? 1 : -1,
        alpha: Math.random() * 0.5 + 0.5, // Higher base alpha
        connectionStrength: Math.random() * 0.7 + 0.3, // Stronger connections
      }));
      
      function animate() {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        // Draw connections first
        for (let i = 0; i < particles.length; i++) {
          const p = particles[i];
          for (let j = i + 1; j < particles.length; j++) {
            const other = particles[j];
            const dist = Math.hypot(p.x - other.x, p.y - other.y);
            
            // Increase connection distance and visibility
            if (dist > 0 && dist < 100) {
              const opacityBase = 0.35; // Higher base opacity
              const dynamicOpacity = (1 - dist / 100) * opacityBase;
              const finalOpacity = dynamicOpacity * p.alpha * other.alpha;
              
              const combinedStrength = (p.brightness * p.connectionStrength + other.brightness * other.connectionStrength) / 2;
              const lineWidth = 0.4 + combinedStrength * 1.2; // Thicker lines
              
              // Brighter gradient for better visibility
              const gradient = ctx.createLinearGradient(p.x, p.y, other.x, other.y);
              gradient.addColorStop(0, `rgba(130, 173, 207, ${finalOpacity * 1.4})`);
              gradient.addColorStop(0.5, `rgba(200, 230, 255, ${finalOpacity * 1.8})`);
              gradient.addColorStop(1, `rgba(130, 173, 207, ${finalOpacity * 1.4})`);
              
              ctx.beginPath();
              ctx.strokeStyle = gradient;
              ctx.lineWidth = lineWidth;
              ctx.moveTo(p.x, p.y);
              ctx.lineTo(other.x, other.y);
              ctx.stroke();
            }
          }
        }
        
        // Draw particles
        particles.forEach(p => {
          // Pulse effect
          p.alpha += p.pulse * p.pulseDirection;
          if (p.alpha > 0.9) {
            p.alpha = 0.9;
            p.pulseDirection = -1;
          } else if (p.alpha < 0.3) {
            p.alpha = 0.3;
            p.pulseDirection = 1;
          }
          
          // Larger glow for better visibility
          const glowRadius = p.radius * 3.5;
          const gradient = ctx.createRadialGradient(
            p.x, p.y, 0,
            p.x, p.y, glowRadius
          );
          
          // Brighter colors for better visibility
          gradient.addColorStop(0, `rgba(200, 230, 255, ${p.alpha})`);
          gradient.addColorStop(0.5, `rgba(130, 173, 207, ${p.alpha * 0.6})`);
          gradient.addColorStop(1, 'rgba(40, 80, 120, 0)');
          
          ctx.beginPath();
          ctx.fillStyle = gradient;
          ctx.arc(p.x, p.y, glowRadius, 0, Math.PI * 2);
          ctx.fill();
          
          // Core particle - brighter
          ctx.beginPath();
          ctx.fillStyle = `rgba(220, 240, 255, ${p.alpha * p.brightness})`;
          ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
          ctx.fill();
          
          // Move particle
          p.x += p.dx;
          p.y += p.dy;
          
          // Bounce off edges
          if (p.x > canvas.width || p.x < 0) p.dx = -p.dx;
          if (p.y > canvas.height || p.y < 0) p.dy = -p.dy;
        });
        
        requestAnimationFrame(animate);
      }
      
      animate();
      
      // Handle window resize
      window.addEventListener('resize', () => {
        const sidebar = this.renderRoot.querySelector('.sidebar-container');
        if (sidebar) {
          canvas.width = sidebar.offsetWidth;
          canvas.height = sidebar.offsetHeight;
        } else {
          canvas.width = 80;
          canvas.height = window.innerHeight;
        }
      });
    }, 100); // Short delay to ensure DOM is ready
  }

  renderAuthenticatedNav() {
    const permissions = this.permissionsReady ? this.effectivePermissions : null;
    if (this.authenticated && (!this.permissionsReady || !permissions)) {
      return html`
        <style>
          .sidebar-loading {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 14px;
            padding-top: 42px;
            animation: sidebarFade 0.4s ease;
          }

          .sidebar-loading-dot {
            width: 10px;
            height: 10px;
            border-radius: 9999px;
            background: rgba(255, 255, 255, 0.28);
            animation: sidebarPulse 1.2s ease-in-out infinite;
          }

          .sidebar-loading-dot:nth-child(2) {
            animation-delay: 0.14s;
          }

          .sidebar-loading-dot:nth-child(3) {
            animation-delay: 0.28s;
          }

          @keyframes sidebarPulse {
            0%, 100% { transform: scale(0.85); opacity: 0.35; }
            50% { transform: scale(1.2); opacity: 0.9; }
          }

          @keyframes sidebarFade {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
          }
        </style>
        <div class="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-20 lg:bg-gray-900 lg:pb-4 sidebar-container relative">
          <canvas id="sidebarCanvas" class="absolute inset-0" style="opacity: 0.3; pointer-events: none;"></canvas>
          <div class="sidebar-loading">
            <div class="sidebar-loading-dot"></div>
            <div class="sidebar-loading-dot"></div>
            <div class="sidebar-loading-dot"></div>
          </div>
        </div>
        <div class="lg:pl-20">
          <div class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white/95 backdrop-blur px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
            <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
              <div class="flex items-center gap-x-4 lg:gap-x-6 ml-auto">
                <div class="h-9 w-9 rounded-full bg-gray-200 animate-pulse"></div>
                <div class="hidden sm:block h-3 w-16 rounded bg-gray-200 animate-pulse"></div>
              </div>
            </div>
          </div>
        </div>
      `;
    }

    const canAccess = (permissionKey) => hasSidebarAccess(permissions, permissionKey);

    return html`
      <style>
        .sidebar-nav-item {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 10px;
          border-radius: 8px;
          margin: 0 auto;
          width: 40px;
          height: 40px;
        }

        .sidebar-nav-item:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-radius: 8px;
        }
      </style>
      <div class="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:z-50 lg:block lg:w-20 lg:bg-gray-900 lg:pb-4 sidebar-container relative">
        <canvas id="sidebarCanvas" class="absolute inset-0" style="opacity: 0.6; pointer-events: none;"></canvas>
        <div class="flex h-20 shrink-0 items-center justify-center relative z-10">
          <a href="/me" class="relative">
            <img class="h-16" src="/assets/kablewy_spark.png" alt="Kablewy">
          </a>
        </div>
        <nav class="flex h-[calc(100%-80px)] flex-col justify-between relative z-10">
          <ul role="list" class="flex flex-col items-center space-y-1">
            ${canAccess('chat') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/me" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'chat')}
                >
                  <svg class="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 0 1 .865-.501 48.172 48.172 0 0 0 3.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Chat</span>
                    <span class="text-xs">Interact with your AI productivity partner to get help with tasks, answer questions, think through problems, and provide insights.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            <!--<li class="tooltip-container w-full flex justify-center">
              <a href="/tools" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
               >
               <svg class="h-8 w-8 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                 <path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75a4.5 4.5 0 01-4.884 4.484c-1.076-.091-2.264.071-2.95.904l-7.152 8.684a2.548 2.548 0 11-3.586-3.586l8.684-7.152c.833-.686.995-1.874.904-2.95a4.5 4.5 0 016.336-4.486l-3.276 3.276a3.004 3.004 0 002.25 2.25l3.276-3.276c.256.565.398 1.192.398 1.852z" />
                 <path stroke-linecap="round" stroke-linejoin="round" d="M4.867 19.125h.008v.008h-.008v-.008z" />
               </svg>
               <div class="tooltip-right tooltip">
                 <span class="font-medium">AI Tools</span>
                 <span class="text-xs">Create custom tools and manage unified integrations (BYOK, Pipedream, native).</span>
               </div>
             </a>
            </li>-->
            ${canAccess('documents') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/documents" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'documents')}
               >
                  <svg class="h-8 w-8 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Documents</span>
                    <span class="text-xs">Upload, share, and search your documents to power AI interactions with your unstructured data.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            ${canAccess('queues') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/queues" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'queues')}
                >
                  <svg class="h-8 w-8 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Queues</span>
                    <span class="text-xs">Queues enable AI models to interact with your event queues, allowing models to process event data, create workflows, and manage asynchronous task execution all under your supervision and control.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            ${canAccess('graph') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/graph" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'graph')}
               >
                  <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#aaa">
                    <path d="M480-80q-50 0-85-35t-35-85q0-5 .5-11t1.5-11l-83-47q-16 14-36 21.5t-43 7.5q-50 0-85-35t-35-85q0-50 35-85t85-35q24 0 45 9t38 25l119-60q-3-23 2.5-45t19.5-41l-34-52q-7 2-14.5 3t-15.5 1q-50 0-85-35t-35-85q0-50 35-85t85-35q50 0 85 35t35 85q0 20-6.5 38.5T456-688l35 52q8-2 15-3t15-1q17 0 32 4t29 12l66-54q-4-10-6-20.5t-2-21.5q0-50 35-85t85-35q50 0 85 35t35 85q0 50-35 85t-85 35ZM200-320q17 0 28.5-11.5T240-360q0-17-11.5-28.5T200-400q-17 0-28.5 11.5T160-360q0 17 11.5 28.5T200-320Zm160-400q17 0 28.5-11.5T400-760q0-17-11.5-28.5T360-800q-17 0-28.5 11.5T320-760q0 17 11.5 28.5T360-720Zm120 560q17 0 28.5-11.5T520-200q0-17-11.5-28.5T480-240q-17 0-28.5 11.5T440-200q0 17 11.5 28.5T480-160Zm40-320q17 0 28.5-11.5T560-520q0-17-11.5-28.5T520-560q-17 0-28.5 11.5T480-520q0 17 11.5 28.5T520-480Zm240-200q17 0 28.5-11.5T800-720q0-17-11.5-28.5T760-760q-17 0-28.5 11.5T720-720q0 17 11.5 28.5T760-680Z"/>
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Knowledge Graph</span>
                    <span class="text-xs">Visualize relationships between entities in a structured way that enables complex reasoning, context awareness, and scalable knowledge organization that mirrors how humans understand the world.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            ${canAccess('tables') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/tables" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'tables')}
                >
                  <svg class="h-8 w-8 shrink-0" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M13.125 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M20.625 12c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5M12 14.625v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 14.625c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125m0 1.5v-1.5m0 0c0-.621.504-1.125 1.125-1.125m0 0h7.5" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Tables</span>
                    <span class="text-xs">Access, query, analyze, and share structured data through interactive tables.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            ${canAccess('artifacts') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/artifacts" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'artifacts')}
                >
                  <svg class="h-8 w-8 shrink-0"  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Artifacts</span>
                    <span class="text-xs">Manage your created artifacts and generate new signed URLs for sharing visual content, documents, and interactive applications.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            ${canAccess('media') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/media"
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'media')}
               >
                  <svg class="h-8 w-8 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75A2.25 2.25 0 016 4.5h12a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0118 19.5H6A2.25 2.25 0 013.75 17.25V6.75z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" d="M7.5 14.25l3-3 2.25 2.25L16.5 9.75"/>
                    <circle cx="9" cy="8.25" r="1.125" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Media</span>
                    <span class="text-xs">Generate, edit and compose images using AI.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            ${canAccess('browser') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/browser"
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'browser')}
               >
                  <svg class="h-8 w-8 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5a17.92 17.92 0 0 1-8.716-2.247m0 0A8.966 8.966 0 0 1 3 12c0-1.97.633-3.791 1.708-5.272" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Browser</span>
                    <span class="text-xs">Automate web tasks with AI-powered browser workflows.</span>
                  </div>
                </a>
              </li>
            ` : ''}
          </ul>

          <ul role="list" class="flex flex-col items-center space-y-1">
            ${canAccess('metrics') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/metrics" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'metrics')}
               >
                  <svg class="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6a7.5 7.5 0 1 0 7.5 7.5h-7.5V6Z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 10.5H21A7.5 7.5 0 0 0 13.5 3v7.5Z" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Metrics</span>
                    <span class="text-xs">Basic stats on documents and knowledge graph.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            <!-- <li class="tooltip-container w-full flex justify-center">
              <a href="/opfs" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
               >
                <svg class="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3V6a3 3 0 013-3h13.5a3 3 0 013 3v5.25a3 3 0 01-3 3m-16.5 0a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25h15a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25h-15z" />
                </svg>
                <div class="tooltip-right tooltip">
                  <span class="font-medium">Offline AI</span>
                  <span class="text-xs">Complete offline AI workspace: Local LLMs + MCP tools + data sync - full desktop app functionality in browser.</span>
                </div>
              </a>
            </li> -->
            ${canAccess('settings') ? html`
              <li class="tooltip-container w-full flex justify-center">
              <a href="/settings" 
                class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold w-full items-center justify-center"
                @mouseover=${this._showTooltip}
                @mouseout=${this._hideTooltip}
                @click=${(e) => this._handleNavClick(e, 'settings')}
               >
                  <svg class="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a6.759 6.759 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Settings</span>
                    <span class="text-xs">Configure your account preferences, connect with your favorite apps, and manage your account and user settings.</span>
                  </div>
                </a>
              </li>
            ` : ''}
            ${canAccess('help') ? html`
              <li class="tooltip-container w-full flex justify-center">
                <button 
                  @click=${(e) => { if (this._handleNavClick(e, 'help')) { this.helpSidebarOpen = true; } }}
                  class="sidebar-nav-item text-gray-400 hover:text-white group flex gap-x-3 rounded-md p-3 text-sm leading-6 font-semibold w-full items-center justify-center border-none bg-transparent cursor-pointer"
                  @mouseover=${this._showTooltip}
                  @mouseout=${this._hideTooltip}
                 >
                  <svg class="h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                  </svg>
                  <div class="tooltip-right tooltip">
                    <span class="font-medium">Help & Guide</span>
                    <span class="text-xs">Learn how to use Kablewy features and get quick tips</span>
                  </div>
                </button>
              </li>
            ` : ''}
          </ul>
        </nav>
      </div>

      <!-- Top Bar -->
      <div class="lg:pl-20">
        <div class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <div class="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div class="flex items-center gap-x-4 lg:gap-x-6 ml-auto">
              <!-- Profile dropdown -->
              <div class="relative">
                <button 
                  @click=${() => this.menuOpen = !this.menuOpen} 
                  class="flex items-center gap-x-4 text-sm font-medium leading-6 text-gray-900" 
                  aria-expanded=${this.menuOpen}
                >
                  ${this.user?.email}
                  <svg class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                  </svg>
                </button>

                ${this.menuOpen ? html`
                  <div 
                    class="absolute right-0 z-10 mt-2.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
                    role="menu"
                  >
                    <a 
                      href="/settings" 
                      class="block px-3 py-1 text-sm leading-6 text-gray-900 hover:bg-gray-50"
                      role="menuitem"
                      @click=${() => this.menuOpen = false}
                    >
                      Profile
                    </a>
                    <button 
                      @click=${this.handleLogout}
                      class="block w-full px-3 py-1 text-left text-sm leading-6 text-gray-900 hover:bg-gray-50"
                      role="menuitem"
                    >
                      Sign out
                    </button>
                  </div>
                ` : ''}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderPublicNav() {
    return html`
      <header class="bg-white">
        <nav class="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
          <div class="flex lg:flex-1">
            <a href="/" class="-m-1.5 relative">
              <img class="h-10 w-auto" src="/assets/kablewy-horz.png" alt="Kablewy">
            </a>
          </div>

          <!-- Mobile menu button -->
          <div class="flex lg:hidden">
            <button 
              @click=${() => this.mobileMenuOpen = !this.mobileMenuOpen}
              class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
            >
              <span class="sr-only">Open main menu</span>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />
            </svg>

            </button>
          </div>

          <div class="hidden lg:flex lg:gap-x-12">
            <!-- <a href="/docs" class="text-sm font-semibold leading-6 text-gray-900">Documentation</a>
            <a href="/faq" class="text-sm font-semibold leading-6 text-gray-900">FAQ</a>
            <a href="/contact" class="text-sm font-semibold leading-6 text-gray-900">Contact</a> -->
          </div>

          <div class="hidden lg:flex lg:flex-1 lg:justify-end">
            <!-- <a 
              href="/login"
              class="text-sm font-semibold leading-6 text-gray-900"
            >
              Sign in <span aria-hidden="true">&rarr;</span>
            </a> -->
          </div>
        </nav>

        <!-- Mobile menu -->
        ${this.mobileMenuOpen ? html`
          <div class="lg:hidden" role="dialog" aria-modal="true">
            <div class="fixed inset-0 z-10"></div>
            <div class="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
              <div class="flex items-center justify-between">
                <a href="/" class="-m-1.5 p-1.5">
                  <img class="h-8 w-auto" src="/assets/kablewy_spark.png" alt="Kablewy">
                </a>
                <button
                  @click=${() => this.mobileMenuOpen = false}
                  class="-m-2.5 rounded-md p-2.5 text-gray-700"
                >
                  <span class="sr-only">Close menu</span>
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
                </button>
              </div>
              <div class="mt-6 flow-root">
                <div class="-my-6 divide-y divide-gray-500/10">
                  <div class="space-y-2 py-6">
                    <a
                      href="/docs"
                      class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      Documentation
                    </a>
                    <a
                      href="/faq"
                      class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      FAQ
                    </a>
                    <a
                      href="/contact"
                      class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      Contact
                    </a>
                  </div>
                  <div class="py-6">
                    <a
                      href="/login"
                      class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                    >
                      Sign in
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ` : ''}
      </header>
    `;
  }
  
  render() {
    return html`
      <div class="min-h-screen bg-white" @show-notification=${this._handleShowNotification}>
        ${this.authenticated ? this.renderAuthenticatedNav() : this.renderPublicNav()}
        
        <main
          id="outlet"
          class="${this.authenticated ? 'lg:pl-20' : ''} min-h-screen bg-white relative flex-1 overflow-y-auto focus:outline-none"
          tabindex="0"
        ></main>
        
        <!-- Global drag and drop for file uploads -->
        ${this.authenticated ? html`<global-drag-drop></global-drag-drop>` : ''}

        <!-- Global toast host -->
        <toast-notifications></toast-notifications>

        <!-- Help sidebar -->
        ${this.authenticated ? html`
          <help-sidebar 
            .isOpen=${this.helpSidebarOpen}
            @close=${() => this.helpSidebarOpen = false}
          ></help-sidebar>
        ` : ''}
      </div>
    `;
  }

  _showTooltip(e) {
    const container = e.target.closest('.tooltip-container');

    const tooltip = container.querySelector('.tooltip');

    tooltip.classList.add('visible');

  }

  _hideTooltip(e) {
    const container = e.target.closest('.tooltip-container');
    const tooltip = container.querySelector('.tooltip');
    tooltip.classList.remove('visible');
  }

  _safeParsePermissions(value) {
    if (!value || typeof value !== 'string') return null;
    try {
      return JSON.parse(value);
    } catch (error) {
      console.warn('[AppRoot] Failed to parse permissions JSON.', error);
      return null;
    }
  }

  _seedPermissionsFromSession() {
    try {
      const session = tokenProvider.getSessionData?.();
      if (!session?.userData) {
        return;
      }
      const role = session.userData.role || 'member';
      const raw = session.userData.permissions || this._safeParsePermissions(session.userData.permissions_json);
      if (!raw) {
        return;
      }
      const normalized = normalizeMemberPermissions(raw, role);
      if (normalized) {
        this._setEffectivePermissions(normalized, true);
      }
    } catch (error) {
      console.warn('[AppRoot] Unable to seed permissions from session.', error);
    }
  }

  _setEffectivePermissions(permissions, ready = true) {
    if (!permissions) return;
    const current = this.effectivePermissions;
    const same =
      current &&
      (() => {
        try {
          return JSON.stringify(current) === JSON.stringify(permissions);
        } catch (_) {
          return false;
        }
      })();
    if (same && this.permissionsReady === ready) {
      return;
    }
    this.setState({ effectivePermissions: permissions, permissionsReady: ready });
  }

  _ensureEffectivePermissions(state) {
    if (!state?.authenticated) {
      if (state.permissionsReady && state.effectivePermissions === null) return;
      this.setState({ effectivePermissions: null, permissionsReady: true });
      return;
    }
    if (state.permissionsReady && state.effectivePermissions) {
      return;
    }

    const profile = state.currentMemberProfile || this.currentMemberProfile;
    const session = tokenProvider.getSessionData?.();
    const role = profile?.role || state.user?.role || session?.userData?.role || 'member';

    let rawPermissions =
      profile?.permissions ||
      this._safeParsePermissions(profile?.permissions_json) ||
      session?.userData?.permissions ||
      this._safeParsePermissions(session?.userData?.permissions_json);

    if (!rawPermissions) {
      return;
    }

    const normalized = normalizeMemberPermissions(rawPermissions, role);
    this._setEffectivePermissions(normalized, true);
  }
}

customElements.define("app-root", AppRoot);
